import { ArrowContainer } from 'react-tiny-popover';
import React, { useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import { CircularCheckmark } from '@/components/circular-checkmark';
import { HSpace, VSpace } from '@/components/spacing';
import { BigButton } from '@/components/big-button';
import { DownIcon } from '@/features/details/date-pick/chip';
import {
  ChipText,
  Container,
  PopoverContainer,
  Row,
} from '@/features/details/sessions-type-chip';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { Tooltip } from '@/components/tooltip';
import { Experience, TargetingDeviceType } from '@/webapi/use-experience-api';
import { maybe } from '@/features/details/utils';
import { ResponsivePopover } from '@/components/responsive-popover';
import { Chip } from '@/features/details/shared';

export function AudiencesChip({ experience }: { experience: Experience }) {
  const [open, setOpen] = useState(false);

  const [audience, setAudience] = useQueryParam<string>(`aud`, StringParam);
  const notAvailable = isAudiencesNotAvailable(experience.metricsStartAt);

  const isDeviceSelectionDisabled = shouldDisableDeviceSelection(experience);
  const isNewRetAudienceDisabled =
    shouldDisableNewRetVisitorsSelection(experience);
  const onSave = () => {
    setAudience(current || ``);
    setOpen(false);
  };

  const [current, setCurrent] = useState<string>(audience);
  if (notAvailable) {
    return null;
  }

  return (
    <ResponsivePopover
      onClickOutside={() => setOpen(!open)}
      isOpen={open}
      positions={[`bottom`, `left`, `right`]}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="white"
          arrowSize={20}
        >
          <PopoverContainer>
            <Row>
              <CircularCheckmark
                disabled={isDeviceSelectionDisabled}
                onChange={(sel) => {
                  setCurrent(sel ? `m` : ``);
                }}
                selected={current === `m`}
                caption="Mobile Visitors"
              />
              <Tooltip title="Mobile Visitors" text="Mobile visitors only" />
            </Row>
            <VSpace value={2} />
            <Row>
              <CircularCheckmark
                disabled={isDeviceSelectionDisabled}
                onChange={(sel) => {
                  setCurrent(sel ? `d` : ``);
                }}
                selected={current === `d`}
                caption="Desktop Visitors"
              />
              <Tooltip title="Desktop Visitors" text="Desktop visitors only" />
            </Row>
            <VSpace value={2} />
            <Row>
              <CircularCheckmark
                disabled={isNewRetAudienceDisabled}
                onChange={(sel) => {
                  setCurrent(sel ? `r` : ``);
                }}
                selected={current === `r`}
                caption="Returning Visitors"
              />
              <Tooltip
                title="Desktop Visitors"
                text="Revisiting visitors, showing continued interest and engagement"
              />
            </Row>
            <VSpace value={2} />
            <Row>
              <CircularCheckmark
                disabled={isNewRetAudienceDisabled}
                onChange={(sel) => {
                  setCurrent(sel ? `n` : ``);
                }}
                selected={current === `n`}
                caption="New Visitors"
              />
              <Tooltip
                title="New Visitors"
                text="First-time visitors to the site"
              />
            </Row>
            <VSpace value={2} />
            <Row>
              <CircularCheckmark
                onChange={(sel) => {
                  sel && setCurrent(``);
                }}
                selected={!current}
                caption="No Filter"
              />
            </Row>
            <VSpace value={3} />
            <BigButton
              onClick={onSave}
              color="white"
              background="#3451fa"
              noTransform
              fillWidth
              type="submit"
              size="medium"
            >
              Save
            </BigButton>
          </PopoverContainer>
        </ArrowContainer>
      )}
    >
      <Container>
        <HSpace />
        <Chip onClick={() => setOpen(!open)}>
          <ChipText> {getAudiencesChipText(audience)}</ChipText>
          <DownIcon />
        </Chip>
      </Container>
    </ResponsivePopover>
  );
}

function getAudiencesChipText(audience: string | undefined) {
  switch (audience) {
    case `m`:
      return `Mobile`;
    case `d`:
      return `Desktop`;
    case `r`:
      return `Returning Visitors`;
    case `n`:
      return `New Visitors`;
    default:
      return `No Filter`;
  }
}

function isAudiencesNotAvailable(metricsStart: string) {
  if (
    window?.location?.hostname ===
      `pre-prod--kind-noether-230989.netlify.app` ||
    window?.location?.hostname === `vsly.local`
  ) {
    return false;
  }
  return !(
    useFeatureBit(FeatureBit.CUSTOM_AUDIENCES) &&
    new Date(metricsStart).valueOf() >= new Date(`2024-06-22`).valueOf()
  );
}
function shouldDisableNewRetVisitorsSelection(experience: Experience) {
  const segments = experience?.targeting?.other?.flatMap((x) =>
    x.values.map((v) => (x.qbProps.envKey.includes(`Segments`) ? v.value : ``)),
  );
  return maybe(
    () =>
      experience?.targeting?.segments?.includes(`RETURNING_VISITORS`) ||
      experience?.targeting?.segments?.includes(`FIRST_SESSION`) ||
      segments?.includes(`FIRST_SESSION`) ||
      segments?.includes(`RETURNING_VISITORS`),
  );
}
function shouldDisableDeviceSelection(experience: Experience) {
  const devices = experience?.targeting?.other?.flatMap((x) =>
    x?.values?.map((v) => (x.qbProps.envKey.includes(`Device`) ? v.value : ``)),
  );

  return maybe(
    () =>
      devices?.includes(`m`) ||
      devices?.includes(`d`) ||
      experience.targeting.device === TargetingDeviceType.MOBILE ||
      experience.targeting.device === TargetingDeviceType.DESKTOP,
  );
}
